import fetchCommerceToolsGraphQLApi from "@secureo/common/utils/commerceTools/graphql/fetchCommerceToolsGraphQLApi"
import {
	getWhereConditionForIsVisible,
	getWhereConditionForExcludedShopCodesKeyNotIn,
	getWhereConditionForCategoryId,
	getWhereConditionForLockingSystemComponentTypeIn,
} from "@secureo/common/utils/commerceTools/graphql/whereConditions/product"
import getIncludeNamesFilter from "../getIncludeNamesFilter"
import formatLockingSystemPreviewProduct from "@secureo/common/utils/commerceTools/formatters/formatLockingSystemPreviewProduct"

import { Product } from "@secureo/common/typings/generated/commercetools-graphql"
import { LockingSystemPreviewProductsByTypeBySku } from "@secureo/common/typings/LockingSystem"
import { CurrencyCode } from "@secureo/common/typings/Forex"

const masterVariantAttributesRawIncludeNames = [
	"lockingSystemComponentType",
	"lockingSystemCharges",
	"lockingSystemComponentName",
]

const getWhereCondition = (lockingSystemCategoryId: string) => {
	const whereCondition = `"${getWhereConditionForCategoryId(
		lockingSystemCategoryId,
	)} and ${getWhereConditionForExcludedShopCodesKeyNotIn([
		process.env.NEXT_PUBLIC_COMMERCETOOLS_STORE_KEY as any,
	])} and (${getWhereConditionForIsVisible()} or ${getWhereConditionForLockingSystemComponentTypeIn(
		["key", "accessory"],
	)})"`

	return whereCondition
}

const fetchLockingSystemPreviewProducts = async (
	accessToken: string,
	lockingSystemCategoryId: string,
	currencyCode: CurrencyCode,
) => {
	const whereCondition = getWhereCondition(lockingSystemCategoryId)

	const query = /* GraphQL */ `
		query lockingSystemPreviewProducts {
			products(where: ${whereCondition}, limit: 100) {
				results {
					id
					masterData {
						current {
							name(locale: "de")
							masterVariant {
								sku
								prices {
									channel {
										key
									}
									country
									value {
										centAmount
										currencyCode
									}
								}
								images {
									url
								}
								attributesRaw(includeNames: [${getIncludeNamesFilter(masterVariantAttributesRawIncludeNames)}]
								) {
									name
									value
								}
							}
						}
					}
				}
			}
		}
	`

	const data = (await fetchCommerceToolsGraphQLApi(accessToken, query)) as {
		products: {
			results: Product[]
		}
	}

	const lockingSystemPreviewProducts = data?.products?.results ?? []
	const formattedLockingSystemPreviewProducts = lockingSystemPreviewProducts.map(
		(lockingSystemPreviewProduct) =>
			formatLockingSystemPreviewProduct(lockingSystemPreviewProduct, currencyCode),
	)

	const lockingSystemPreviewProductsByTypeBySku: LockingSystemPreviewProductsByTypeBySku =
		formattedLockingSystemPreviewProducts.reduce(
			(lockingSystemPreviewProductsByTypeBySku, formattedLockingSystemPreviewProduct) => {
				const { sku, lockingSystemComponentType } = formattedLockingSystemPreviewProduct

				const prevProductsBySkuForType =
					lockingSystemPreviewProductsByTypeBySku?.[lockingSystemComponentType] ?? {}

				return {
					...lockingSystemPreviewProductsByTypeBySku,
					[lockingSystemComponentType]: {
						...prevProductsBySkuForType,
						[sku]: formattedLockingSystemPreviewProduct,
					},
				}
			},
			{ lock: {}, key: {}, accessory: {} } as LockingSystemPreviewProductsByTypeBySku,
		)

	return lockingSystemPreviewProductsByTypeBySku
}

export default fetchLockingSystemPreviewProducts
